import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Skeleton,
  Checkbox,
  Button,
  Snackbar,
  Alert,
  TextField,
} from "@mui/material";
import { Close, Paid, Replay, Delete, TaskAlt } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { TASKS_API_BASE } from "./apiConfig";
dayjs.extend(isoWeek);

const TASKS_API_BASE_URL = TASKS_API_BASE;
const getScadentarKey = (row) =>
  row?.issue_key || row?.id || row?.issueId || row?.issue_id || null;
const getPaymentStatus = (row) => String(row?.statusPlata ?? row?.status ?? "").trim();
const FINAL_PAYMENT_STATUSES = new Set(["achitat", "anulat", "returnat", "whitelist"]);

const parseMoneyToNumber = (raw) => {
  if (raw === null || raw === undefined || raw === "") return null;

  if (typeof raw === "number") {
    return Number.isFinite(raw) ? raw : null;
  }

  let str = String(raw)
    .replace(/RON|Lei|lei/gi, "")
    .replace(/\u00A0/g, " ")
    .replace(/\s+/g, "")
    .trim();

  const hasComma = str.includes(",");
  const hasDot = str.includes(".");
  if (hasComma && hasDot) {
    str = str.replace(/\./g, "").replace(",", ".");
  } else if (hasComma) {
    str = str.replace(",", ".");
  } else if (hasDot) {
    const parts = str.split(".");
    const lastPart = parts[parts.length - 1] || "";
    const looksLikeThousands =
      parts.length > 2 || (lastPart.length === 3 && parts[0].length > 1);
    if (looksLikeThousands) {
      str = str.replace(/\./g, "");
    }
  }

  const num = Number(str);
  return Number.isFinite(num) ? num : null;
};

const getRenegotiatedAmountValue = (row) =>
  parseMoneyToNumber(row?.sumaRenegociata ?? row?.suma_renegociata ?? null);

const formatAmountInputValue = (value) => {
  const amount = parseMoneyToNumber(value);
  return amount === null ? "" : String(amount);
};

const ScadentarDetails = ({
  selectedRow,
  onClose,
  onUpdate,
  onDeleted,
  onNotify,
}) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [expandedStatus, setExpandedStatus] = useState(null);

  const [partialAmount, setPartialAmount] = useState("");
  const [partialPayments, setPartialPayments] = useState([]);
  const [loadingPartial, setLoadingPartial] = useState(false);
  const [renegotiatedAmount, setRenegotiatedAmount] = useState("");
  const [savingRenegotiatedAmount, setSavingRenegotiatedAmount] = useState(false);
  const [sendingRenegotiatedEmail, setSendingRenegotiatedEmail] = useState(false);
  const [deletingTask, setDeletingTask] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [notifyClient, setNotifyClient] = useState(true);
  const handleCloseSnackbar = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };
  const showEmailToast = (toast) => {
    if (onNotify) {
      onNotify(toast);
      return;
    }
    setSnackbar(toast);
  };

  const productImageSource =
    selectedRow?.image_url ||
    selectedRow?.product_image ||
    selectedRow?.image ||
    selectedRow?.imageUrl ||
    selectedRow?.main_image ||
    selectedRow?.thumbnail;
  const productImageBase64 = selectedRow?.image_base64;
  const productImage =
    productImageSource ||
    (productImageBase64
      ? productImageBase64.startsWith("data:")
        ? productImageBase64
        : `data:image/jpeg;base64,${productImageBase64}`
      : null);

  useEffect(() => {
    setRenegotiatedAmount(
      formatAmountInputValue(getRenegotiatedAmountValue(selectedRow))
    );
  }, [selectedRow]);

  useEffect(() => {
    const fetchDeadline = async () => {
      const scadentarKey = getScadentarKey(selectedRow);
      if (!scadentarKey) {
        setSelectedDate(null);
        return;
      }

      // 1) Dacă avem deja deadlinePlata în selectedRow (din listă), folosim imediat
      if (selectedRow.deadlinePlata) {
        const quick = dayjs(selectedRow.deadlinePlata, "YYYY-MM-DD", true);
        setSelectedDate(
          quick.isValid()
            ? quick
            : dayjs(selectedRow.deadlinePlata).isValid()
              ? dayjs(selectedRow.deadlinePlata)
              : null
        );
      } else {
        setSelectedDate(null);
      }

      // 2) Citim din endpointul de detalii care include deadlinePlata în payload
      try {
        const resp = await fetch(
          `${TASKS_API_BASE_URL}/scadentar/${scadentarKey}`
        );
        const data = await resp.json();
        const raw = data?.deadlinePlata;
        if (Object.prototype.hasOwnProperty.call(data || {}, "sumaRenegociata")) {
          setRenegotiatedAmount(
            formatAmountInputValue(getRenegotiatedAmountValue(data))
          );
        }
        if (raw) {
          const parsedStrict = dayjs(raw, "YYYY-MM-DD", true);
          const parsed = parsedStrict.isValid()
            ? parsedStrict
            : dayjs(raw).isValid()
              ? dayjs(raw)
              : null;
          setSelectedDate(parsed);
        }
      } catch (err) {
        console.error("Eroare la citirea deadlinePlata din detalii:", err);
      }
    };

    fetchDeadline();
  }, [selectedRow]);

  useEffect(() => {
    const scadentarKey = getScadentarKey(selectedRow);
    if (!scadentarKey) {
      setPartialPayments([]);
      return;
    }
    setLoadingPartial(true);
    fetch(
      `${TASKS_API_BASE_URL}/scadentar/${scadentarKey}/partial-payments`
    )
      .then((r) => r.json())
      .then((data) => setPartialPayments(Array.isArray(data) ? data : []))
      .catch((err) => console.error("❌ Eroare partial-payments GET:", err))
      .finally(() => setLoadingPartial(false));
  }, [selectedRow]);

  const formatRON = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const amount = parseMoneyToNumber(value);
    if (amount === null) return "-";
    return new Intl.NumberFormat("ro-RO", {
      style: "currency",
      currency: "RON",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getBankLogo = (iban) => {
    if (!iban || !iban.startsWith("RO") || iban.length < 8) return null;
    const logos = {
      BTRL: "/logos/banca_transilvania.png",
      RZBR: "/logos/raiffeisen.png",
      BCRL: "/logos/bcr.png",
      INGB: "/logos/ing.png",
      BRDE: "/logos/brd.png",
      PIRB: "/logos/firstbank.png",
      REVO: "/logos/revo.png",
    };
    const code = iban.slice(4, 8).toUpperCase();
    return logos[code] || null;
  };

  const getOriginalSumaPlataValue = () =>
    selectedRow?.customfield_10181 ||
    selectedRow?.customfield_10072 ||
    selectedRow?.pret_intrare ||
    selectedRow?.sumaPlataExacta ||
    selectedRow?.sumaPlata ||
    null;

  const getSumaPlataValue = () =>
    getRenegotiatedAmountValue(selectedRow) ?? getOriginalSumaPlataValue();

  const handleSaveRenegotiatedAmount = async () => {
    const scadentarKey = getScadentarKey(selectedRow);
    if (!scadentarKey) return;

    const trimmed = String(renegotiatedAmount || "").trim();
    const amount = trimmed === "" ? null : parseMoneyToNumber(trimmed);

    if (trimmed !== "" && (amount === null || amount < 0)) {
      setSnackbar({
        open: true,
        message: "Introduceți o sumă RE-negociată validă",
        severity: "warning",
      });
      return;
    }

    setSavingRenegotiatedAmount(true);
    try {
      const resp = await fetch(
        `${TASKS_API_BASE_URL}/scadentar/${encodeURIComponent(
          scadentarKey
        )}/renegotiated-amount`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sumaRenegociata: amount }),
        }
      );
      const responseText = await resp.text();
      let data = {};
      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch {
        data = {};
      }
      if (!resp.ok) {
        throw new Error(
          data?.error || responseText || "Eroare la salvarea sumei"
        );
      }

      const savedAmount = data?.sumaRenegociata ?? amount;
      setRenegotiatedAmount(formatAmountInputValue(savedAmount));
      if (onUpdate) {
        onUpdate({
          ...selectedRow,
          sumaRenegociata: savedAmount,
        });
      }
      setSnackbar({
        open: true,
        message:
          savedAmount === null
            ? "Suma RE-negociat a fost ștearsă"
            : "Suma RE-negociat a fost salvată",
        severity: "success",
      });
    } catch (err) {
      console.error("Eroare la salvarea sumaRenegociata:", err);
      setSnackbar({
        open: true,
        message: err.message || "Eroare la salvarea sumei RE-negociate",
        severity: "error",
      });
    } finally {
      setSavingRenegotiatedAmount(false);
    }
  };

  const handleSendRenegotiatedEmail = async () => {
    const scadentarKey = getScadentarKey(selectedRow);
    if (!scadentarKey) return;

    const trimmed = String(renegotiatedAmount || "").trim();
    const amount = trimmed === "" ? null : parseMoneyToNumber(trimmed);

    if (amount === null || amount <= 0) {
      showEmailToast({
        open: true,
        message: "Introduceți o sumă RE-negociată validă înainte de email",
        severity: "warning",
      });
      return;
    }

    setSendingRenegotiatedEmail(true);
    try {
      const saveResp = await fetch(
        `${TASKS_API_BASE_URL}/scadentar/${encodeURIComponent(
          scadentarKey
        )}/renegotiated-amount`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sumaRenegociata: amount }),
        }
      );
      const saveText = await saveResp.text();
      let saveData = {};
      try {
        saveData = saveText ? JSON.parse(saveText) : {};
      } catch {
        saveData = {};
      }
      if (!saveResp.ok) {
        throw new Error(
          saveData?.error || saveText || "Eroare la salvarea sumei"
        );
      }

      const savedAmount = saveData?.sumaRenegociata ?? amount;
      setRenegotiatedAmount(formatAmountInputValue(savedAmount));
      if (onUpdate) {
        onUpdate({
          ...selectedRow,
          sumaRenegociata: savedAmount,
        });
      }

      const emailResp = await fetch(
        `${TASKS_API_BASE_URL}/scadentar/${encodeURIComponent(
          scadentarKey
        )}/renegotiated-amount/email`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sumaRenegociata: savedAmount }),
        }
      );
      const emailText = await emailResp.text();
      let emailData = {};
      try {
        emailData = emailText ? JSON.parse(emailText) : {};
      } catch {
        emailData = {};
      }
      if (!emailResp.ok) {
        throw new Error(
          emailData?.error || emailText || "Eroare la trimiterea emailului"
        );
      }

      showEmailToast({
        open: true,
        message: "Emailul cu suma RE-negociată a fost trimis",
        severity: "success",
      });
    } catch (err) {
      console.error("Eroare la trimiterea emailului de renegociere:", err);
      showEmailToast({
        open: true,
        message: err.message || "Eroare la trimiterea emailului",
        severity: "error",
      });
    } finally {
      setSendingRenegotiatedEmail(false);
    }
  };

  const handleDeleteTask = async () => {
    const scadentarKey = getScadentarKey(selectedRow);
    if (!scadentarKey || deletingTask) return;

    if (!window.confirm("Ești sigur că vrei să ștergi acest task?")) {
      return;
    }

    setDeletingTask(true);
    try {
      const resp = await fetch(
        `${TASKS_API_BASE_URL}/scadentar/${encodeURIComponent(scadentarKey)}`,
        { method: "DELETE" }
      );
      const data = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        throw new Error(data?.error || "Eroare la ștergerea taskului");
      }

      if (onDeleted) {
        onDeleted({
          id: scadentarKey,
          row: selectedRow,
          message: "Planificarea la plată din scadentar a fost ștearsă.",
        });
      }

      if (onClose) onClose();
    } catch (err) {
      console.error("❌ Eroare la ștergerea taskului:", err);
      setSnackbar({
        open: true,
        message: err.message || "Eroare la ștergerea taskului",
        severity: "error",
      });
    } finally {
      setDeletingTask(false);
    }
  };

  const handleDateChange = async (newDate) => {
    setSelectedDate(newDate);
    const scadentarKey = getScadentarKey(selectedRow);
    if (!newDate || !scadentarKey) return;

    const currentStatus = getPaymentStatus(selectedRow);
    const shouldSetPlanned = !FINAL_PAYMENT_STATUSES.has(currentStatus.toLowerCase());
    const sendEmail = shouldSetPlanned
      ? window.confirm("Vrei să trimitem email clientului cu planificarea?")
      : false;

    try {
      // 1) Salvăm data de planificare
      await fetch(
        `${TASKS_API_BASE_URL}/scadentar/${scadentarKey}/deadline`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ deadlinePlata: newDate.format("YYYY-MM-DD") }),
        }
      );

      // 2) Setăm statusul la Planificat doar pentru statusuri nefinale.
      const sumaPlata = getSumaPlataValue();
      if (shouldSetPlanned) {
        await fetch(
          `${TASKS_API_BASE_URL}/scadentar/${scadentarKey}/status`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              statusPlata: "Planificat",
              cc: "suport@luxuraelite.ro",
              notifyClient: !!sendEmail,
              sumaPlata,
              dataPlata: newDate.format("DD/MM/YYYY"),
            }),
          }
        );
      }

      // 3) Updatăm UI-ul local
      if (onUpdate) {
        onUpdate({
          ...selectedRow,
          deadlinePlata: newDate.format("YYYY-MM-DD"),
          statusPlata: shouldSetPlanned ? "Planificat" : currentStatus,
        });
      }

      // 4) Snackbar de confirmare
      setSnackbar({
        open: true,
        message: shouldSetPlanned
          ? "Data salvată. Status setat pe Planificat."
          : "Data salvată. Statusul de plată a rămas neschimbat.",
        severity: "success",
      });
    } catch (err) {
      console.error("Eroare la salvarea deadline/status:", err);
      setSnackbar({
        open: true,
        message: "Eroare la salvarea datei sau a statusului.",
        severity: "error",
      });
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    const scadentarKey = getScadentarKey(selectedRow);
    if (!scadentarKey) return;
    const sumaPlata = getSumaPlataValue();
    const rawStatusPlataValue = String(newStatus || "").trim();
    const isWhitelistStatus = rawStatusPlataValue.toLowerCase() === "whitelist";
    const statusPlataValue = isWhitelistStatus ? "Whitelist" : rawStatusPlataValue;
    const shouldNotifyClient = isWhitelistStatus ? false : notifyClient;
    try {
      await fetch(
        `${TASKS_API_BASE_URL}/scadentar/${scadentarKey}/status`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            statusPlata: statusPlataValue,
            notifyClient: shouldNotifyClient,
            cc: "suport@luxuraelite.ro",
            sumaPlata,
          }),
        }
      );
      if (onUpdate) {
        onUpdate({ ...selectedRow, statusPlata: newStatus });
      }
      setSnackbar({
        open: true,
        message: `Status actualizat: ${newStatus}`,
        severity: "success",
      });
    } catch (err) {
      console.error("Eroare la salvarea statusPlata:", err);
      setSnackbar({
        open: true,
        message: "Eroare la actualizarea statusului",
        severity: "error",
      });
    }
  };

  const handleAddPartialPayment = async () => {
    const val = Number(
      String(partialAmount)
        .replace(/[^0-9.,]/g, "")
        .replace(",", ".")
    );
    if (!val || Number.isNaN(val) || val <= 0) {
      setSnackbar({
        open: true,
        message: "Introduceți o sumă validă",
        severity: "warning",
      });
      return;
    }
    try {
      const resp = await fetch(
        `${TASKS_API_BASE_URL}/scadentar/${getScadentarKey(selectedRow)}/partial-payments`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount: val,
            paid_at: new Date().toISOString(),
          }),
        }
      );
      const item = await resp.json();
      if (!resp.ok) throw new Error(item?.error || "Eroare");
      setPartialPayments((prev) => [item, ...prev]);
      setPartialAmount("");
      setSnackbar({
        open: true,
        message: "Plată parțială înregistrată",
        severity: "success",
      });
    } catch (e) {
      console.error("❌ Eroare partial-payments POST:", e);
      setSnackbar({
        open: true,
        message: "Eroare la înregistrarea plății",
        severity: "error",
      });
    }
  };

  if (!selectedRow) return null;

  const iban = selectedRow.iban || "";

  return (
    <Box
      sx={{
        width: "40%",
        ml: 2,
        backgroundColor: "#fff",
        boxShadow: 3,
        p: 3,
        borderRadius: 2,
        height: "100%",
        maxHeight: "80vh",
        overflowY: "auto",
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        p={2}
        border="1px solid #f0f0f0"
        borderRadius={2}
        mb={3}
        sx={{ backgroundColor: "#f9f9f9" }}
      >
        <Box display="flex" alignItems="center">
          <Avatar sx={{ bgcolor: "#1976d2", width: 56, height: 56, mr: 2 }}>
            {(selectedRow.customfield_10067 || selectedRow.vanzator)
              ?.split(" ")
              .map((word) => word[0])
              .slice(0, 2)
              .join("")
              .toUpperCase() || "?"}
          </Avatar>
          <Box display="flex" flexDirection="column" justifyContent="center">
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              color="text.primary"
            >
              {selectedRow.customfield_10067 || selectedRow.vanzator || "—"}
            </Typography>
            <Typography variant="body2" color="text.primary">
              {selectedRow.telefon || "—"}
            </Typography>
            <Typography variant="body2" color="text.primary">
              {selectedRow.email || "—"}
            </Typography>
          </Box>
        </Box>

        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </Box>
      {iban && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{ mt: "-15px" }}
        >
          <Avatar
            alt="bank logo"
            src={getBankLogo(iban) || ""}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "";
              e.target.style.backgroundColor = "#ccc";
              e.target.alt = "fallback";
            }}
            sx={{
              width: 24,
              height: 24,
              mr: 1,
              bgcolor: getBankLogo(iban) ? "transparent" : "#ccc",
              "& img": {
                objectFit: "contain",
                padding: 2,
              },
            }}
          />
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            color="text.primary"
          >
            {iban}
          </Typography>
        </Box>
      )}
      <Typography
        variant="subtitle2"
        fontWeight="bold"
        mb={0.5}
        color="text.primary"
      >
        Detalii
      </Typography>

      <Box
        mt={0.5}
        p={2}
        bgcolor="#f5f5f5"
        border="1px solid #ddd"
        borderRadius={2}
      >
        <Box
          display="grid"
          gridTemplateColumns="1fr auto"
          alignItems="center"
          gap={2}
        >
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            color="text.primary"
          >
            Planificare Plată
          </Typography>
          <Box display="flex" alignItems="center" sx={{ maxWidth: 160 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD/MM/YYYY"
                value={selectedDate}
                onChange={handleDateChange}
                shouldDisableDate={(date) => {
                  const day = date.day();
                  return day === 0 || day === 6;
                }}
                slotProps={{
                  textField: {
                    variant: "standard",
                    InputProps: {
                      disableUnderline: true,
                      sx: {
                        border: "none",
                        padding: 0,
                        width: "100%",
                        maxWidth: 122,
                        ml: 0,
                        textAlign: "right",
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
        </Box>

        <Box
          mt={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            color="text.primary"
          >
            Suma de plată
          </Typography>
          <Typography variant="subtitle1" color="text.primary">
            {formatRON(getOriginalSumaPlataValue())}
          </Typography>
        </Box>
        <Box
          mt={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            color="text.primary"
          >
            Planificat la plată
          </Typography>
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            color="text.primary"
          >
            {selectedDate
              ? `Saptamana ${selectedDate.isoWeek()} - An ${selectedDate.year()}`
              : "—"}
          </Typography>
        </Box>
      </Box>
      <Typography
        variant="subtitle2"
        fontWeight="bold"
        mt={3}
        mb={0.5}
        color="text.primary"
      >
        Suma RE-negociat
      </Typography>
      <Box
        mt={0.5}
        p={2}
        bgcolor="#f5f5f5"
        border="1px solid #ddd"
        borderRadius={2}
      >
        <Box
          display="flex"
          gap={1}
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
        >
          <TextField
            value={renegotiatedAmount}
            onChange={(e) => setRenegotiatedAmount(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSaveRenegotiatedAmount();
              }
            }}
            placeholder="Introdu sumă (RON)"
            size="small"
            inputProps={{ inputMode: "decimal" }}
            sx={{ flex: "1 1 180px", bgcolor: "#fff", borderRadius: 1 }}
          />
          <Button
            variant="contained"
            size="small"
            disableElevation
            disabled={savingRenegotiatedAmount || sendingRenegotiatedEmail}
            onClick={handleSaveRenegotiatedAmount}
            sx={{
              backgroundColor: "#f3e5f5",
              color: "#6a1b9a",
              textTransform: "none",
              fontWeight: "bold",
              borderRadius: 2,
              "&:hover": { backgroundColor: "#e1bee7" },
            }}
          >
            Salvează
          </Button>
          <Button
            variant="outlined"
            size="small"
            disableElevation
            disabled={savingRenegotiatedAmount || sendingRenegotiatedEmail}
            onClick={handleSendRenegotiatedEmail}
            sx={{
              bgcolor: "#fff",
              borderColor: "#6a1b9a",
              color: "#6a1b9a",
              textTransform: "none",
              fontWeight: "bold",
              borderRadius: 2,
              whiteSpace: "nowrap",
              "&:hover": {
                bgcolor: "#f3e5f5",
                borderColor: "#6a1b9a",
              },
            }}
          >
            Send email
          </Button>
        </Box>
      </Box>
      <Typography
        variant="subtitle2"
        fontWeight="bold"
        mt={3}
        mb={0.5}
        color="text.primary"
      >
        Plăți parțiale
      </Typography>
      <Box
        mt={0.5}
        p={2}
        bgcolor="#f5f5f5"
        border="1px solid #ddd"
        borderRadius={2}
      >
        <Box
          display="flex"
          gap={1}
          alignItems="center"
          justifyContent="space-between"
        >
          <TextField
            value={partialAmount}
            onChange={(e) => setPartialAmount(e.target.value)}
            placeholder="Introdu sumă (RON)"
            size="small"
            inputProps={{ inputMode: "decimal" }}
            sx={{ flex: 1, bgcolor: "#fff", borderRadius: 1 }}
          />
          <Button
            variant="contained"
            size="small"
            disableElevation
            onClick={handleAddPartialPayment}
            sx={{
              backgroundColor: "#e8f5e9",
              color: "#2e7d32",
              textTransform: "none",
              fontWeight: "bold",
              borderRadius: 2,
              "&:hover": { backgroundColor: "#c8e6c9" },
            }}
          >
            Înregistrează plata parțială
          </Button>
        </Box>

        <Box mt={2}>
          {loadingPartial ? (
            <Typography variant="body2" color="text.secondary">
              Se încarcă…
            </Typography>
          ) : partialPayments.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nu există plăți parțiale.
            </Typography>
          ) : (
            <Box display="flex" flexDirection="column" gap={1}>
              {partialPayments.map((p) => (
                <Box
                  key={p.id}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  p={1}
                  bgcolor="#fff"
                  borderRadius={1}
                >
                  <Typography variant="body2" color="text.secondary">
                    {dayjs(p.paid_at).format("DD.MM.YYYY HH:mm")}
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatRON(p.amount)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      <Typography
        variant="subtitle2"
        fontWeight="bold"
        mt={3}
        mb={0.5}
        color="text.primary"
      >
        Detalii Produs
      </Typography>
      <Box
        mt={0.5}
        p={2}
        bgcolor="#f5f5f5"
        border="1px solid #ddd"
        borderRadius={2}
      >
        {productImage && (
          <Box
            mb={2}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Box
              component="img"
              src={productImage}
              alt={selectedRow?.produs || "Produs"}
              sx={{
                maxWidth: "100%",
                maxHeight: 220,
                borderRadius: 2,
                objectFit: "cover",
                border: "1px solid #e0e0e0",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = "none";
              }}
            />
          </Box>
        )}
        <Typography variant="body1" color="text.primary">
          {selectedRow.produs || "-"}
        </Typography>
        <Box
          mt={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            color="text.primary"
          >
            Nr Jurnal
          </Typography>
          <Typography variant="subtitle1" color="text.primary">
            {selectedRow.customfield_10066 || selectedRow.nr_jurnal || "-"}
          </Typography>
        </Box>
        <Box
          mt={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            color="text.primary"
          >
            SKU
          </Typography>
          <Typography variant="subtitle1" color="text.primary">
            {selectedRow.customfield_10083 || selectedRow.sku || "-"}
          </Typography>
        </Box>
        <Box
          mt={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            color="text.primary"
          >
            Tip Contract
          </Typography>
          <Typography
            variant="subtitle1"
            color="text.primary"
            sx={{ minWidth: "130px", textAlign: "right" }}
          >
            {selectedRow.customfield_10183_value ||
              selectedRow.tip_contract ||
              "-"}
          </Typography>
        </Box>
      </Box>
      <Box mt={3} display="flex" justifyContent="space-between" gap={2}>
        <Box
          onClick={() => setExpandedStatus("anulat")}
          display="flex"
          alignItems="center"
          px={2}
          py={1}
          bgcolor="#ffebee"
          borderRadius={2}
          width="100%"
          sx={{ cursor: "pointer" }}
        >
          <Close sx={{ color: "#c62828", mr: 1 }} />
          <Typography variant="body2" color="#c62828" fontWeight="bold">
            Anulat
          </Typography>
        </Box>
        <Box
          onClick={() => setExpandedStatus("achitat")}
          display="flex"
          alignItems="center"
          px={2}
          py={1}
          bgcolor="#e8f5e9"
          borderRadius={2}
          width="100%"
          sx={{ cursor: "pointer" }}
        >
          <Paid sx={{ color: "#2e7d32", mr: 1 }} />
          <Typography variant="body2" color="#2e7d32" fontWeight="bold">
            Achitat
          </Typography>
        </Box>
        <Box
          onClick={() => setExpandedStatus("returnat")}
          display="flex"
          alignItems="center"
          px={2}
          py={1}
          bgcolor="#e3f2fd"
          borderRadius={2}
          width="100%"
          sx={{ cursor: "pointer" }}
        >
          <Replay sx={{ color: "#1565c0", mr: 1 }} />
          <Typography variant="body2" color="#1565c0" fontWeight="bold">
            Returnat
          </Typography>
        </Box>
        <Box
          onClick={() => handleStatusUpdate("Whitelist")}
          display="flex"
          alignItems="center"
          px={2}
          py={1}
          bgcolor="#fff8e1"
          borderRadius={2}
          width="100%"
          sx={{ cursor: "pointer" }}
        >
          <TaskAlt sx={{ color: "#f9a825", mr: 1 }} />
          <Typography variant="body2" color="#f9a825" fontWeight="bold">
            Whitelist
          </Typography>
        </Box>
      </Box>
      {expandedStatus === "anulat" && (
        <Box
          mt={3}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Checkbox
              size="small"
              checked={notifyClient}
              onChange={(e) => setNotifyClient(e.target.checked)}
            />
            <Typography variant="body2">Informează client</Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            disableElevation
            onClick={() => handleStatusUpdate("Anulat")}
            sx={{
              backgroundColor: "#ffebee",
              color: "#c62828",
              fontSize: "0.875rem",
              fontWeight: "bold",
              textTransform: "none",
              padding: "8px 16px",
              borderRadius: 16,
              boxShadow: "none",
              "&:hover": { backgroundColor: "#ffcdd2", boxShadow: "none" },
            }}
          >
            Anulat &amp; Trimite
          </Button>
        </Box>
      )}
      {expandedStatus === "achitat" && (
        <Box
          mt={3}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Checkbox
              size="small"
              checked={notifyClient}
              onChange={(e) => setNotifyClient(e.target.checked)}
            />
            <Typography variant="body2">Informează client</Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            disableElevation
            onClick={() => handleStatusUpdate("Achitat")}
            sx={{
              backgroundColor: "#e8f5e9",
              color: "#2e7d32",
              fontSize: "0.875rem",
              fontWeight: "bold",
              textTransform: "none",
              padding: "8px 16px",
              borderRadius: 16,
              boxShadow: "none",
              "&:hover": { backgroundColor: "#c8e6c9", boxShadow: "none" },
            }}
          >
            Achitat &amp; Trimite
          </Button>
        </Box>
      )}
      {expandedStatus === "returnat" && (
        <Box
          mt={3}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Checkbox
              size="small"
              checked={notifyClient}
              onChange={(e) => setNotifyClient(e.target.checked)}
            />
            <Typography variant="body2">Informează client</Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            disableElevation
            onClick={() => handleStatusUpdate("Returnat")}
            sx={{
              backgroundColor: "#e3f2fd",
              color: "#1565c0",
              fontSize: "0.875rem",
              fontWeight: "bold",
              textTransform: "none",
              padding: "8px 16px",
              borderRadius: 16,
              boxShadow: "none",
              "&:hover": { backgroundColor: "#bbdefb", boxShadow: "none" },
            }}
          >
            Returnat &amp; Trimite
          </Button>
        </Box>
      )}
      {/* Buton X de ștergere da */}
      <Box mt={4} display="flex" justifyContent="center">
        <IconButton
          disabled={deletingTask}
          onClick={handleDeleteTask}
          sx={{
            backgroundColor: "#ffebee",
            color: "#c62828",
            opacity: deletingTask ? 0.6 : 1,
            "&:hover": {
              backgroundColor: "#ffcdd2",
            },
          }}
        >
          <Delete />
        </IconButton>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ScadentarDetails;
